/**
 * Historial de chats (seccion 11, capa 1).
 *
 * Las conversaciones ya viven en Postgres desde el primer turno; lo que
 * faltaba era poder volver a ellas. Aqui se traduce lo que devuelve el API al
 * vocabulario propio del cliente, sin pasar por el SDK: los mensajes
 * recuperados no son un stream, son datos.
 */
import {
  queryOptions,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { Schemas } from "../lib/api-schemas.ts";

import {
  readTurnUsage,
  totalTokens,
  type UsageTotals,
} from "../usage/usage.ts";
import type {
  AgentMessage,
  AgentPart,
  ConfirmationResult,
} from "./agent-event.ts";
import { isAwaitingConfirmation } from "./decisions.ts";
import { CHAT_TEXTS as TEXTS } from "./texts.ts";

export type ConversationSummary = Readonly<Schemas["ConversationSummary"]>;

/**
 * Un mensaje tal y como lo guarda el backend, y sus partes.
 *
 * `MessagePart` es una union DISCRIMINADA por `type`: cada variante trae solo
 * sus campos, no una bolsa de opcionales. Eso es lo que hace que la traduccion
 * de abajo no tenga que defenderse de un `toolName` ausente en una parte de
 * texto -- no existe -- y que anadir una variante en el backend rompa aqui.
 */
type StoredMessage = Schemas["ConversationMessage"];

/**
 * La conversacion TAL Y COMO LLEGA, que no es lo mismo que como se promete.
 *
 * El contrato declara `awaiting` obligatorio, y lo normal es que venga. Pero
 * un tipo generado describe lo que el backend promete, no lo que entra por el
 * cable: un despliegue a medias o un fallo lo dejan fuera, y darlo por hecho
 * significa que el historial ENTERO revienta por un campo auxiliar. Se marca
 * opcional aqui, en la frontera, y se resuelve una sola vez.
 *
 * Lo mismo vale para `usage`: sin él, el historial se abre y el contador
 * suma lo que traigan los mensajes.
 *
 * Sin el campo, nada consta como pendiente y las tarjetas se dibujan inertes.
 * Es la degradacion segura: una confirmacion ya resuelta con sus botones
 * puestos seria una trampa; una pendiente sin botones, solo una molestia.
 */
type WireConversation = Omit<Schemas["Conversation"], "awaiting" | "usage"> &
  Partial<Pick<Schemas["Conversation"], "awaiting" | "usage">>;

export interface LoadedConversation {
  readonly id: string;
  readonly title: string | null;
  /** Resumen de los turnos compactados, si la conversación es larga. */
  readonly summary: string | null;
  readonly messages: readonly AgentMessage[];
  /** Cuántos mensajes del principio están resumidos. */
  readonly compactedCount: number;
  /** Lo que lleva gastado, según el backend, si lo dice. */
  readonly usage?: UsageTotals;
}

export const listConversations = (): Promise<readonly ConversationSummary[]> =>
  request<readonly ConversationSummary[]>(`${API_BASE}/conversations`);

/**
 * La clave del historial, en un solo sitio.
 *
 * La comparten quien lo lee, quien lo invalida al terminar un turno y las dos
 * mutaciones de aquí abajo. Escrita a mano en cada sitio, una letra de más
 * deja la lista sin refrescar y nadie se entera hasta que alguien renombra.
 */
export const CONVERSATIONS_KEY = ["conversations"] as const;

/**
 * El historial de conversaciones.
 *
 * Se invalida al terminar un turno --es cuando puede haber una conversación
 * nueva o un título recién puesto-- en vez de recargarse en cada render.
 */
export const conversationsQuery = () =>
  queryOptions({
    queryKey: CONVERSATIONS_KEY,
    queryFn: listConversations,
  });

/**
 * Lo que deja un renombrado: la FILA del historial, no la conversación.
 *
 * El tipo sale del contrato, así que no cuesta nada tenerlo; lo que no se hace
 * es consumirlo. Aquí no se lee ni un campo: quien enseña el título es la
 * lista, que se vuelve a pedir igual, y meter esta respuesta en la caché sería
 * una segunda vía hacia lo mismo. Lo que dice el contrato y conviene saber es
 * que `title` puede llegar `null` --un título que al sanearlo queda vacío no
 * pisa el que hubiera-- y que `updatedAt` NO cambia al renombrar: renombrar es
 * mantenimiento, no actividad, y la conversación no salta en el historial.
 */
type RenamedConversation = Schemas["RenamedConversation"];

/**
 * Renombrar a mano y borrar (contrato acordado con el backend).
 *
 * El borrado no devuelve nada --un 204-- porque de lo que ya no existe no hay
 * nada que contar. Y el 404 de las dos tapa también "es de otro usuario": no
 * hay forma de distinguirlas desde aquí, ni falta.
 */
export const renameConversation = (
  id: string,
  title: string,
): Promise<RenamedConversation> =>
  request<RenamedConversation>(`${API_BASE}/conversations/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });

export const deleteConversation = (id: string): Promise<void> =>
  request<undefined>(`${API_BASE}/conversations/${id}`, { method: "DELETE" });

/** Cómo se llama una conversación que todavía no tiene nombre. */
export const UNTITLED_CONVERSATION = TEXTS.conversations.untitled;

/**
 * Lo que cabe en la fila del historial sin partirse.
 *
 * Es el mismo tope que aplica el backend al titular, y lo deja dicho el propio
 * contrato: el `title` del PATCH admite más caracteres a propósito para que
 * uno de más sea un título recortado y no un 422, porque quien recorta --a
 * 80-- es el dominio. El número está duplicado aquí, como el formato: son dos
 * repositorios independientes y no se va a publicar un paquete por un entero.
 * Sirve para que el campo no deje escribir lo que el servidor va a recortar.
 */
export const TITLE_MAX_LENGTH = 80;

/** Cada cuánto se vuelve a preguntar por el título, y hasta cuándo. */
const TITLE_POLL_MS = 3_000;
const TITLE_WAIT_MS = 30_000;

/**
 * Una conversación sin nombre puede llegar como `null` o como cadena vacía;
 * para quien la dibuja son lo mismo, así que aquí se vuelven lo mismo.
 */
export const namedTitle = (title: string | null | undefined): string | null => {
  const clean = title?.trim() ?? "";
  return clean === "" ? null : clean;
};

/**
 * Si el nombre que hay escrito se puede guardar.
 *
 * En blanco no es un título: sería quitarle el nombre a la conversación, y de
 * eso ya se encarga el automático. Y guardar lo mismo que ya había es pedirle
 * trabajo al servidor para no cambiar nada.
 */
export const canSaveTitle = (
  draft: string,
  current: string | null,
): boolean => {
  const clean = draft.trim();
  return clean !== "" && clean !== (current ?? "");
};

/** El título de una conversación dentro del historial ya cargado. */
export const titleOf = (
  conversations: readonly ConversationSummary[] | undefined,
  conversationId: string | undefined,
): string | null =>
  namedTitle(
    conversations?.find((conversation) => conversation.id === conversationId)
      ?.title,
  );

/**
 * Cuánto esperar antes de volver a preguntar por el título, o `false` para
 * dejar de preguntar.
 *
 * Se deja de insistir por lo obvio --ya tiene nombre-- y también al agotar la
 * espera: un título que no ha llegado en medio minuto no va a llegar (el
 * backend se rinde en silencio si el primer mensaje no da para titular), y un
 * sondeo perpetuo por cada pestaña abierta no lo vale.
 *
 * Y se calla mientras alguien está renombrando a mano. El sondeo relee el
 * historial entero, así que una lectura lanzada un instante antes del PATCH
 * llega después con el título viejo y le pisa a esa persona lo que acaba de
 * escribir. Es una carrera que no hace falta correr: quien pregunta "¿ya tiene
 * nombre?" no necesita respuesta mientras se lo están poniendo.
 */
export const titleRefetchDelay = (
  title: string | null,
  waitedMs: number,
  renaming = false,
): number | false =>
  renaming || title !== null || waitedMs >= TITLE_WAIT_MS
    ? false
    : TITLE_POLL_MS;

/**
 * El historial con una conversación renombrada, y el historial sin una
 * conversación.
 *
 * Son funciones puras --y no dos `setQueryData` escritos a mano-- porque son
 * lo que ve el usuario antes de que el servidor conteste: la lista se cambia
 * al instante y, si la petición falla, se vuelve a la de antes. Aquí es donde
 * se puede comprobar que ninguna de las dos toca las demás filas.
 */
export const withTitle = (
  conversations: readonly ConversationSummary[],
  id: string,
  title: string,
): readonly ConversationSummary[] =>
  // Solo el título, y ese es justo el alcance del servidor: `updatedAt` no
  // cambia al renombrar, así que la fila se queda donde estaba en vez de
  // saltar al principio del historial mientras se la mira.
  conversations.map((conversation) =>
    conversation.id === id ? { ...conversation, title } : conversation,
  );

export const withoutConversation = (
  conversations: readonly ConversationSummary[],
  id: string,
): readonly ConversationSummary[] =>
  conversations.filter((conversation) => conversation.id !== id);

/**
 * La clave de la mutación de renombrar.
 *
 * No es decorativa: es como el sondeo del título automático se entera de que
 * hay un renombrado en marcha (ver `titleRefetchDelay`).
 */
export const RENAME_MUTATION_KEY = ["conversations", "rename"] as const;

/**
 * Renombrar a mano, con la lista cambiada antes de que el servidor conteste.
 *
 * El renombrado se hace desde la fila que se está mirando, así que esperar
 * medio segundo a que vuelva la respuesta se nota. Se cambia la caché al
 * momento y el fallo devuelve la fila a su sitio: una lista que miente sobre
 * lo que hay guardado es peor que un error visible.
 *
 * El otro lado de la carrera --el título automático pisando este-- lo resuelve
 * el backend, no esto: `retitle` pisa lo que hubiera porque lo ha pedido
 * alguien, y `autotitle` no pisa nunca un título que ya existe. Gane quien
 * gane la carrera, el nombre puesto a mano es el que queda.
 */
export const useRenameConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: RENAME_MUTATION_KEY,
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      renameConversation(id, title),

    onMutate: async ({ id, title }) => {
      // Cancelar ANTES de tocar la caché: una lectura del historial que salió
      // hace un instante --el sondeo del título, o la invalidación de fin de
      // turno-- volvería con el nombre viejo y borraría este.
      await queryClient.cancelQueries({ queryKey: CONVERSATIONS_KEY });

      const previous =
        queryClient.getQueryData<readonly ConversationSummary[]>(
          CONVERSATIONS_KEY,
        );
      queryClient.setQueryData(
        CONVERSATIONS_KEY,
        withTitle(previous ?? [], id, title.trim()),
      );

      return { previous };
    },

    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(CONVERSATIONS_KEY, context.previous);
      }
    },

    // Haya ido bien o mal, la última palabra la tiene el servidor.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
  });
};

/**
 * Borrar.
 *
 * Aquí NO se adelanta la fila, al revés que al renombrar, y no es por
 * prudencia: el diálogo que espera la respuesta vive dentro de la fila. Si la
 * fila se fuera antes de tiempo se llevaría por delante al propio diálogo, y
 * con él las dos cosas que todavía tienen que pasar --avisar de que se ha
 * borrado la conversación abierta, y enseñar el fallo si el servidor dice que
 * no--. Así que la fila se va cuando el servidor confirma; eso sí, sin esperar
 * a releer el historial, porque de algo que ya no existe no hay nada que
 * preguntar.
 *
 * `onDeleted` se pasa al hook y no a `mutate` a propósito: los callbacks de
 * `mutate` no se llaman si el componente se desmonta antes de la respuesta, y
 * este componente se desmonta justo entonces.
 */
export const useDeleteConversation = (onDeleted: (id: string) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteConversation,

    onSuccess: (_result, id) => {
      // Primero el aviso --puede ser la conversación que se está leyendo-- y
      // después la fila, que es lo que desmonta este diálogo.
      onDeleted(id);

      queryClient.setQueryData(
        CONVERSATIONS_KEY,
        withoutConversation(
          queryClient.getQueryData<readonly ConversationSummary[]>(
            CONVERSATIONS_KEY,
          ) ?? [],
          id,
        ),
      );
    },

    // La última palabra la tiene el servidor: si el borrado se cruzó con otra
    // cosa, la relectura lo pone en su sitio.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY }),
  });
};

/**
 * El título de la conversación abierta, en cuanto el backend termina de
 * ponerlo.
 *
 * El título NO viaja por el stream del agente: el backend lo genera con un
 * modelo en un trabajo aparte, fuera del turno, para no retrasar la primera
 * palabra de la respuesta. Suele estar listo cuando ya no queda stream al que
 * engancharse, así que nadie lo empuja y no hay más remedio que preguntar:
 * mientras la conversación abierta siga sin nombre se refresca el historial
 * cada pocos segundos.
 *
 * Se lee de la lista del historial en vez de pedirlo aparte porque el título
 * ya viaja ahí: así la cabecera y la barra lateral dicen lo mismo en el mismo
 * momento, sin dos fuentes que se puedan separar.
 */
export const useConversationTitle = (
  conversationId: string | undefined,
): string | null => {
  // La espera es de ESTA conversación, no de la sesión: al cambiar de chat el
  // reloj vuelve a cero, o el siguiente nacería con el plazo ya agotado.
  const [waitingSince, setWaitingSince] = useState(() => Date.now());
  useEffect(() => {
    setWaitingSince(Date.now());
  }, [conversationId]);

  // Cualquier renombrado a mano en marcha, sea de la conversación abierta o de
  // otra fila: el sondeo relee el historial ENTERO, así que también pisaría el
  // nombre de una conversación que no es la que se está leyendo.
  const renaming = useIsMutating({ mutationKey: RENAME_MUTATION_KEY }) > 0;

  const { data = null } = useQuery({
    ...conversationsQuery(),
    enabled: conversationId !== undefined,
    select: (conversations) => titleOf(conversations, conversationId),
    refetchInterval: (query) =>
      titleRefetchDelay(
        titleOf(query.state.data, conversationId),
        Date.now() - waitingSince,
        renaming,
      ),
  });

  return data;
};

export const loadConversation = async (
  id: string,
): Promise<LoadedConversation> => {
  const conversation = await request<WireConversation>(
    `${API_BASE}/conversations/${id}`,
  );

  /**
   * Qué tarjetas siguen esperando a una persona.
   *
   * El mensaje guardado no lo sabe --es inmutable-- así que lo dice el backend
   * aparte. Todo lo que no esté aquí se dibuja inerte: una confirmación ya
   * aprobada con sus botones sería una trampa.
   */
  const awaiting = new Set([
    ...(conversation.awaiting?.actionIds ?? []),
    ...(conversation.awaiting?.questionIds ?? []),
  ]);

  /**
   * Lo gastado según el libro de consumo, que cuenta desde que existe: una
   * conversación anterior sale a cero. Cero ahí no es "no gastó", es "no
   * consta", así que se deja sin decir y quien lo pinta suma lo que traigan
   * los mensajes.
   */
  const usage =
    conversation.usage !== undefined && totalTokens(conversation.usage) > 0
      ? conversation.usage
      : undefined;

  return {
    id: conversation.id,
    title: conversation.title,
    summary: conversation.summary,
    messages: conversation.messages.map((message) =>
      toAgentMessage(message, awaiting),
    ),
    compactedCount: conversation.messages.filter((message) => message.compacted)
      .length,
    ...(usage !== undefined ? { usage } : {}),
  };
};

/**
 * Un mensaje guardado a uno de la interfaz.
 *
 * La diferencia que importa: al persistir, la llamada a una herramienta y su
 * resultado son DOS partes; en la interfaz son una sola tarjeta. Se emparejan
 * por `callId`.
 */
const toAgentMessage = (
  message: StoredMessage,
  awaiting: ReadonlySet<string>,
): AgentMessage => {
  const parts: AgentPart[] = [];
  const results: ConfirmationResult[] = [];
  const toolIndex = new Map<string, number>();

  for (const part of message.parts) {
    switch (part.type) {
      /**
       * Lo que escribió el SISTEMA para el modelo --"el usuario aprobó…", con
       * el JSON del resultado dentro-- no es texto del usuario y no se pinta
       * (1.7). La aprobación ya la cuentan la tarjeta resuelta y los
       * componentes del resultado; las respuestas, `question_answer`.
       */
      case "text": {
        if (part.text !== "" && part.audience !== "model") {
          parts.push({ type: "text", text: part.text });
        }
        break;
      }

      case "reasoning": {
        if (part.text !== "")
          parts.push({ type: "reasoning", text: part.text });
        break;
      }

      case "confirmation_result": {
        results.push({
          actionId: part.actionId,
          outcome: part.outcome,
          results: part.results,
        });
        break;
      }

      case "question_answer": {
        parts.push({
          type: "question_answer",
          questionId: part.questionId,
          answers: part.answers,
          note: part.note,
        });
        break;
      }

      case "tool_call": {
        toolIndex.set(part.callId, parts.length);
        parts.push({
          type: "tool",
          callId: part.callId,
          toolName: part.toolName,
          // Una llamada sin resultado quedó a medias: el turno se cortó. No se
          // dibuja como "en curso", porque no lo está y no va a estarlo.
          state: "error",
          ...(part.input !== undefined ? { input: part.input } : {}),
          errorText: TEXTS.conversations.interrupted,
        });
        break;
      }

      case "tool_result": {
        const at = toolIndex.get(part.callId);
        const previous = at === undefined ? undefined : parts[at];
        const resolved: AgentPart = {
          type: "tool",
          callId: part.callId,
          toolName: part.toolName,
          state:
            part.isError === true
              ? "error"
              : isAwaitingConfirmation(part.output)
                ? "awaiting"
                : "done",
          ...(previous?.type === "tool" && previous.input !== undefined
            ? { input: previous.input }
            : {}),
          ...(part.output !== undefined ? { output: part.output } : {}),
        };

        if (at === undefined) parts.push(resolved);
        else parts[at] = resolved;
        break;
      }

      /**
       * El enlace a lo que el agente produjo se vuelve a dibujar al abrir la
       * conversación: es tipo e id, no una ruta, así que sigue siendo válido
       * aunque la pantalla se haya movido desde entonces.
       */
      case "resource_link": {
        parts.push({ type: "resource_link", resource: part.resource });
        break;
      }

      case "ui_component": {
        parts.push({
          type: "ui_component",
          render: part.render,
          props: part.props,
        });
        break;
      }

      /**
       * La tarjeta de preguntas se vuelve a dibujar al abrir la conversación.
       *
       * Si viviera solo en el stream, recargar la página dejaría el turno
       * parado esperando una respuesta que ya no se podría dar. El estado está
       * en la base; esto es solo volver a pintarlo.
       */
      case "question_request": {
        parts.push({
          type: "question_request",
          questionId: part.questionId,
          prompt: part.prompt,
          questions: part.questions,
          settled: !awaiting.has(part.questionId),
        });
        break;
      }

      /**
       * La tarjeta de confirmación se vuelve a dibujar igual que la de
       * preguntas. Si viviera solo en el stream, recargar la página dejaría la
       * acción pendiente sin forma de aprobarla ni rechazarla.
       */
      case "confirmation_request": {
        parts.push({
          type: "confirmation_request",
          actionId: part.actionId,
          toolName: part.toolName,
          summary: part.summary,
          input: part.input,
          ...(part.callId !== undefined ? { callId: part.callId } : {}),
          // Un grupo (1.6): varias acciones del mismo paso, una decisión.
          ...(part.items !== undefined ? { items: part.items } : {}),
          settled: !awaiting.has(part.actionId),
        });
        break;
      }

      case "file": {
        parts.push({
          type: "file",
          path: part.path,
          name: part.name,
          mimeType: part.mimeType,
          sizeBytes: part.sizeBytes,
        });
        break;
      }

      /**
       * Un bloque del proveedor que la API exige devolverle tal cual al
       * modelo (la búsqueda de herramientas en el servidor, R3.2). No es de
       * la conversación: ni se dibuja, ni cuenta como herramienta, ni hace
       * que un mensaje tenga contenido. Se ignora A PROPÓSITO y con nombre,
       * no por caer en el `default`, para que una variante nueva de verdad
       * siga sin compilar.
       */
      case "provider_block": {
        break;
      }

      default: {
        /**
         * Una parte que el contrato tiene y aquí no se dibuja.
         *
         * En ejecución se ignora --perder una tarjeta es mejor que romper el
         * hilo entero-- pero en compilación NO pasa: si el backend documenta
         * una variante nueva, `part` deja de ser `never` y esto no compila.
         * Es el aviso de que hay que traducirla, no una red para producción.
         */
        const _unhandled: never = part;
        break;
      }
    }
  }

  const usage = readTurnUsage(message.usage);
  return {
    id: message.id,
    role: message.role,
    parts,
    ...(usage !== undefined ? { usage } : {}),
    ...(results.length > 0 ? { confirmationResults: results } : {}),
  };
};
